import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Grid, Mode, Page, Score } from "../types/Types";

interface BoardProps {
  grid: Grid;
  mode: Mode;
  move: number;
  loading: boolean;
  scores: Score[]
  remainingMoves: number;
  showNextLevel: boolean;
  showOutOfMoves: boolean;
  modeMoves: number;
  setGridOrig: Dispatch<SetStateAction<Grid>>,
  setGrid: Dispatch<SetStateAction<Grid>>,
  setRemainingMoves: Dispatch<SetStateAction<number>>,
  setMove: Dispatch<SetStateAction<number>>,
  updateScore: () => void;
  nextLevel: (mode: Mode,
    setGridOrig: Dispatch<SetStateAction<Grid>>,
    setGrid: Dispatch<SetStateAction<Grid>>,
    setRemainingMoves: Dispatch<SetStateAction<number>>,
    setMove: Dispatch<SetStateAction<number>>,
    reset: boolean
  ) => void;
  setCurrentPage: Dispatch<SetStateAction<Page>>
}

const Board: React.FC<BoardProps> = ({
  grid,
  mode,
  scores,
  move,
  loading,
  remainingMoves,
  showNextLevel,
  showOutOfMoves,
  modeMoves,
  setGridOrig,
  setGrid,
  setRemainingMoves,
  setMove,
  nextLevel,
  updateScore,
  setCurrentPage
}) => {
  
  const width_style = 'calc(100% / ' + grid.length + ')';
  const padding_style = '0 0 calc(100% / ' + grid[0].length + ') 0';

  const [showDropDown, setShowDropdown] = useState(false);

  useEffect(() => { 
    if ( mode === 'daily' )
      updateScore() 
  }, [updateScore, mode]);

  return (
    <div className="relative  grid md:w-[600px] sm:w-[500px] w-[300px]">
      {mode === 'daily' && (
        <div>
          <div onClick={() => setShowDropdown(cur => !cur)} className="font-bold flex items-center mb-1 cursor-pointer">
            <div>Today's top 3</div>
            <svg className={`${showDropDown ? 'rotate-90' : ''} duration-500 transition-all ml-3`} width="10" height="15" viewBox="0 0 10 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.72727 0L0 2.72727L4.54545 7.27273L0 11.8182L2.72727 14.5455L10 7.27273L2.72727 0Z" fill="black" />
            </svg>
          </div>
          <div className={`transition-all duration-500 ease-in-out ${showDropDown ? 'max-h-[300px]' : 'max-h-[0px]'} `}>
            <table className="text-left">
              <thead>
                <tr>
                  <th className="px-3">Rank</th>
                  <th className="px-3">Name</th>
                  <th className="px-3">Score</th>
                </tr>
              </thead>
              <tbody>
                {scores && scores.slice(0,3).map((e: any, i: any) => {
                  return (<tr key={i}>
                    <td className="px-3">{i + 1}</td>
                    <td className="px-3">{e.name}</td>
                    <td className="px-3">{e.score}</td>
                  </tr>)
                })}
                {!scores && (
                  <tr><td></td></tr>
                  )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {mode !== 'daily' && (
        <div className="font-bold cursor-default">
          Level {modeMoves - remainingMoves + 1}
        </div>
      )}
      <div className="relative">
        {grid.map((row:any, rowIndex:any) => (
          <div key={rowIndex} className="row flex w-full">
            {row.map((cell:any, colIndex:any) => (
              <div
              key={colIndex}
              className={`tile bg-cl-${cell}`}
              style={{ 'width': width_style, 'padding': padding_style }}
              ></div>
              ))}
          </div>
        ))}
        {showNextLevel && (
          <div onClick={()=>nextLevel(mode, setGridOrig, setGrid, setRemainingMoves, setMove, false)} 
            className="absolute cursor-pointer left-0 top-0 bottom-0 right-0 bg-black/80 text-white">
            <div className="flex flex-col justify-center text-center h-full">
              <div className="text-3xl">Well done!</div>
              <div className="text-xl">Click here to go to the next level</div>
            </div>
          </div>
        )}
        {showOutOfMoves && (
          <div className="absolute left-0 top-0 bottom-0 right-0 bg-black/80">
            <div className="flex flex-col justify-center text-center h-full text-white">
              <div className="text-3xl">You ran out of moves!</div>
              <div className="text-xl mb-10">You reached level {remainingMoves}</div>
              <div className="flex gap-3 justify-center text-black">
                <button onClick={() => nextLevel(mode, setGridOrig, setGrid, setRemainingMoves, setMove, true)} className='bg-cl-a px-5 py-3 rounded-xl  font-extrabold mt-3'>
                  Play again
                </button>
                <button onClick={() => { setCurrentPage('home'); }} className='bg-cl-b px-5 py-3 rounded-xl  font-extrabold mt-3'>
                  Home
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {mode === 'daily' && (
        <div className="font-bold cursor-default">Move: {move}</div>
      )}
      {mode !== 'daily' && (
        <div className="font-bold cursor-default">Remaining moves: {(remainingMoves - move)}</div>
      )}

      {loading && (
        <div className="absolute w-full h-full bg-black/80">
          <div className="text-white font-4xl flex items-center w-full h-full justify-center">
            <svg className="animate-spin" width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M59.4014 0.079948C59.1615 0.079948 59.0816 0.239844 58.9217 0.319792L28.9412 21.586C28.6214 21.8258 28.1417 22.4654 27.9019 22.8651L26.8625 24.8638C32.6188 26.7026 37.0159 31.0998 38.8547 36.856L40.8534 35.8167C41.2532 35.5769 41.8128 35.1771 42.1326 34.7774L63.3988 4.79688C63.6386 4.39714 63.7186 4.07735 63.3988 3.75756L59.8811 0.239844C59.7212 0.079948 59.5613 0 59.4014 0V0.079948ZM21.1862 31.8193C15.2701 31.8193 10.713 36.6961 10.713 42.5324C10.713 50.4472 6.31589 57.3227 0 61.0004C3.11797 62.7592 6.87553 63.7186 10.713 63.7186C22.4654 63.7186 31.9792 54.2847 31.9792 42.4524C31.9792 36.5363 27.1024 31.7394 21.2662 31.7394L21.1862 31.8193Z" fill="white" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}
 
export default Board;
